import { useEffect, useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Item, ItemContent, ItemTitle } from "@/components/ui/item";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCalendar } from "@/contexts/calendarContext";
import { cn, getInitials, getLocationLabel, sortLocationsByPreference } from "@/lib/utils";
import { ArrowLeftIcon, ArrowRightIcon, ChevronDown, LocateFixed, MapPinHouse, X } from "lucide-react";
import { useNavigate } from "react-router";
import type { Location } from "@/types/Location";
import { defaultButtonClasses, defaultSelectClasses } from "@/constants/ui";

type LocationTumblerProps = {
  disabled?: boolean;
  className?: string;
};

// Sub-component for rendering location items
interface LocationItemProps {
  location: Location;
  isSelected: boolean;
  onSelect: (location: Location) => void;
}

const LocationItem = ({ location, isSelected, onSelect }: LocationItemProps) => (
  <div className="cursor-pointer" onClick={() => onSelect(location)}>
    <Item
      variant="outline"
      className={`
        
        bg-gray-100
        hover:bg-indigo-100
        transition-colors
        mb-1
        ${isSelected ? "bg-indigo-50 border-slate-300" : ""}
      `}
    >
      <ItemContent>
        <div className="flex flex-row items-center gap-3 mb-2">
          <Avatar className="w-12 h-12">
            <AvatarImage
              src={
                location.locationImageUrl ||
                "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
              }
              alt={"Location Avatar"}
            />
            <AvatarFallback>{getInitials(location.name)}</AvatarFallback>
          </Avatar>
          <div>
            <ItemTitle className="text-base">{getLocationLabel(location)}</ItemTitle>
          </div>
        </div>
      </ItemContent>
    </Item>
  </div>
);

const LocationTumbler = ({ disabled = false, className = "" }: LocationTumblerProps) => {
  const { availableLocations, selectedLocation, setSelectedLocation, member, slug, isLocationLoading } = useCalendar();
  const navigate = useNavigate();

  // State for search and popover
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [open, setOpen] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log("[LocationTumbler] State:", {
      availableLocations,
      availableLocationsLength: availableLocations?.length,
      isLocationLoading,
      memberPermissableLocationIds: member?.permissableLocationIds,
      selectedLocation,
      slug,
    });
  }, [availableLocations, isLocationLoading, member?.permissableLocationIds, selectedLocation, slug]);

  const renderNoLocations = () => {
    return (
      <div
        className={`flex flex-row items-center justify-between gap-2 ${className} ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <span>No locations available</span>
      </div>
    );
  };

  // Show loading state while locations are being fetched
  if (isLocationLoading) {
    return (
      <div
        className={`flex flex-row items-center justify-between gap-2 ${className} ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        <span>Loading locations...</span>
      </div>
    );
  }

  // Only show "no locations" if loading is complete and there are truly no locations
  if (!availableLocations || availableLocations.length === 0) {
    return renderNoLocations();
  }

  // Group and sort locations (filtering out inactive locations as a safety measure)
  const { myLocations, otherLocations } = useMemo(() => {
    const memberPermissableLocationIds = member?.permissableLocationIds || [];

    // Filter out inactive locations (backend should already do this, but double-check)
    const activeLocations = availableLocations.filter((loc) => loc.active !== false);

    // Separate locations into "my locations" and "other locations"
    const my: Location[] = [];
    const other: Location[] = [];

    activeLocations.forEach((loc) => {
      if (memberPermissableLocationIds.includes(loc.id)) {
        my.push(loc);
      } else {
        other.push(loc);
      }
    });

    // Preferred locations first (in the member's chosen order), then alphabetical by label.
    const preferred = member?.preferredLocations;
    return {
      myLocations: sortLocationsByPreference(my, preferred),
      otherLocations: sortLocationsByPreference(other, preferred),
    };
  }, [availableLocations, member?.permissableLocationIds, member?.preferredLocations]);

  // Filter locations based on search term
  const { filteredMyLocations, filteredOtherLocations } = useMemo(() => {
    if (!searchTerm.trim()) {
      return {
        filteredMyLocations: myLocations,
        filteredOtherLocations: otherLocations,
      };
    }

    const search = searchTerm.toLowerCase();

    return {
      filteredMyLocations: myLocations.filter(
        (loc) => loc.name.toLowerCase().includes(search) || (loc.city && loc.city.toLowerCase().includes(search)) || (loc.label && loc.label.toLowerCase().includes(search)),
      ),
      filteredOtherLocations: otherLocations.filter(
        (loc) => loc.name.toLowerCase().includes(search) || (loc.city && loc.city.toLowerCase().includes(search)) || (loc.label && loc.label.toLowerCase().includes(search)),
      ),
    };
  }, [myLocations, otherLocations, searchTerm]);

  useEffect(() => {
    if (!selectedLocation && availableLocations.length > 0) {
      // First try to match the location from the URL slug
      const locationFromSlug = slug ? availableLocations.find((loc) => loc.slug === slug) : null;
      const defaultLocation = locationFromSlug || availableLocations[0];

      setSelectedLocation(defaultLocation);
      navigate(`/location/${defaultLocation.slug}/calendar`);
    }
  }, [selectedLocation, availableLocations, navigate, setSelectedLocation, slug]);

  const handlePrevious = () => {
    if (disabled) return;

    // Create a combined list of filtered locations
    const filteredLocations = [...filteredMyLocations, ...filteredOtherLocations];

    if (filteredLocations.length === 0) return;

    const currentIndex = filteredLocations.findIndex((loc) => loc.id === selectedLocation?.id);

    let prevIndex;
    if (currentIndex === -1 || currentIndex === 0) {
      prevIndex = filteredLocations.length - 1;
    } else {
      prevIndex = currentIndex - 1;
    }

    const prevLocation = filteredLocations[prevIndex];
    setSelectedLocation(prevLocation);
    navigate(`/location/${prevLocation.slug}/calendar`);
  };

  const handleNext = () => {
    if (disabled) return;

    // Create a combined list of filtered locations
    const filteredLocations = [...filteredMyLocations, ...filteredOtherLocations];

    if (filteredLocations.length === 0) return;

    const currentIndex = filteredLocations.findIndex((loc) => loc.id === selectedLocation?.id);

    let nextIndex;
    if (currentIndex === -1 || currentIndex === filteredLocations.length - 1) {
      nextIndex = 0;
    } else {
      nextIndex = currentIndex + 1;
    }

    const nextLocation = filteredLocations[nextIndex];
    setSelectedLocation(nextLocation);
    navigate(`/location/${nextLocation.slug}/calendar`);
  };

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    navigate(`/location/${location.slug}/calendar`);
    setOpen(false);
    setSearchTerm(""); // Clear search for next time
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm(""); // Clear search when closing
    }
  };

  return (
    <div
      className={`flex flex-row items-center justify-between gap-2 ${className} ${
        disabled ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <Button
        variant="outline"
        size="icon"
        aria-label="Previous location"
        id="location-previous"
        onClick={handlePrevious}
        disabled={disabled}
        className={defaultButtonClasses}
      >
        <ArrowLeftIcon />
      </Button>

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select location"
            aria-haspopup="listbox"
            className={cn(defaultSelectClasses, "justify-center items-center text-lg min-w-[200px] font-medium [&_span]:truncate flex flex-grow")}
            disabled={disabled}
          >
            <span className="text-lg">
              {selectedLocation ? getLocationLabel(selectedLocation) : "Select location"}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full sm:w-[400px] p-3" align="center" side="bottom">
          {/* Search Input */}
          <div className="relative mb-2">
            <Input
              placeholder="Search locations..."
              aria-label="Search locations"
              className="w-full h-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                <X className="w-4 h-4 text-gray-500 hover:text-gray-700" onClick={() => setSearchTerm("")} />
              </div>
            )}
          </div>

          {/* Location Results */}
          <div className="max-h-[300px] overflow-y-auto" role="listbox">
            {/* My Locations Section */}
            {filteredMyLocations.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold bg-slate-200 rounded sticky top-0 mb-2 z-20">
                  <MapPinHouse className="w-5 h-5" />
                  My Locations ({filteredMyLocations.length})
                </div>
                {filteredMyLocations.map((loc) => (
                  <LocationItem
                    key={loc.id}
                    location={loc}
                    isSelected={selectedLocation?.id === loc.id}
                    onSelect={handleLocationSelect}
                  />
                ))}
              </div>
            )}

            {/* Other Locations Section */}
            {filteredOtherLocations.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-semibold bg-slate-200 rounded sticky top-0 mb-2 z-20">
                  <LocateFixed className="w-5 h-5" />
                  Other Locations ({filteredOtherLocations.length})
                </div>
                {filteredOtherLocations.map((loc) => (
                  <LocationItem
                    key={loc.id}
                    location={loc}
                    isSelected={selectedLocation?.id === loc.id}
                    onSelect={handleLocationSelect}
                  />
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredMyLocations.length === 0 && filteredOtherLocations.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                No locations found matching "{searchTerm}"
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <Button
        variant="outline"
        size="icon"
        aria-label="Next location"
        id="location-next"
        onClick={handleNext}
        disabled={disabled}
        className={defaultButtonClasses}
      >
        <ArrowRightIcon />
      </Button>
    </div>
  );
};

export default LocationTumbler;
